"use client";

import React, { useState, useEffect } from "react";
import { SpacingType, sectionVariants, ButtonStyleType } from "@/types";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';

interface Hero20Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string;
    hasAnimatedText?: boolean;
    animatedText?: {
      text?: string;
    }[];
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    hasStats?: boolean;
    stats?: {
      value?: string;
      label?: string;
    }[];
    hasFeaturedItems?: boolean;
    featuredItems?: {
      icon?: string;
      content?: string;
    }[];
    hasImage?: boolean;
    image?: string;
    imageAltText?: string;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero20 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero20Props) => {

  const words = contents?.animatedText?.map(item => item.text);

  const [text, setText] = useState('your growth');
  const [showCursor, setShowCursor] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [isMounted, setIsMounted] = useState(false);

  // Prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!words || !isMounted) return;
    const handleType = () => {
      const current = loopNum % words.length;
      const fullText = words[current];

      if (!fullText) return;
      setText(isDeleting 
        ? fullText.substring(0, text.length - 1)
        : fullText.substring(0, text.length + 1)
      );

      setTypingSpeed(isDeleting ? 100 : 150);

      if (!isDeleting && text === fullText) {
        setTimeout(() => setIsDeleting(true), 400);
      } else if (isDeleting && text === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    };

    const timer = setTimeout(() => {
      handleType();
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, typingSpeed, words, isMounted]);

  // Cursor blink effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);

    return () => clearInterval(cursorInterval);
  }, []);

  return (
    <SectionWrapper 
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero20"
      attributes={attributes}
      usedInPageNav={false}
      removePaddingBottom={true}
      pageNavTitle="Hero 20"  
      className="bg-position-[50%_100%] overflow-hidden"  
      style={{
        backgroundImage: "url('/hero-coloured-fg-1.svg')",
        backgroundSize: "auto",
        backgroundRepeat: "no-repeat",
      }}
    >     
      <div className="container"> 
        <div className="w-full text-center">
          {contents?.badge && (
            <Badge variant="outline" bold className="mb-6">
              {contents?.badge}
            </Badge>
          )}
          {contents?.title && (
            <h1 className="mb-10 lg:mb-16 text-wrap">
              {parse(contents?.title)}
              {contents?.hasAnimatedText && isMounted && (
                <span className="block md:inline-flex text-brand" suppressHydrationWarning>
                  &nbsp;{text}
                  <span 
                    className={`inline-block w-[2px] bg-brand ${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100 ml-1 align-baseline md:self-end`} 
                    style={{ height: '0.8em' }}
                  >
                  </span>
                </span>
              )}
            </h1>
          )}
          {contents?.content && (
            <div className="paragraph-lg mx-auto !font-normal max-w-[58ch]">
              {parse(contents?.content)}
            </div>
          )}
          {contents?.hasButton && (
            <div className="mt-8 flex flex-col">
              <div className="flex flex-col gap-2 sm:flex-row justify-center">
                <div>
                  <Button 
                    size="lg" 
                    variant={contents?.buttonStyle || "primary"}
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={contents?.buttonLink}>
                      {contents?.buttonText}
                    </a>
                  </Button>
                  {contents?.microCopy && (
                    <div className="paragraph-sm text-muted-foreground text-center mt-2">
                      {contents?.microCopy}
                    </div>
                  )}
                </div>
                {contents?.secondaryButton && (
                  <Button
                    variant={contents?.secondaryButtonStyle || "outline"}
                    size="lg"
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={contents?.secondaryButtonLink}>
                      {contents?.secondaryButtonText}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {contents?.hasStats && (
          <div className="hidden lg:flex flex-col md:flex-row gap-10 items-center pt-10 md:pt-16 pb-10 justify-center">
            {contents?.stats?.map((status, index) => (
              <div key={index} className="flex flex-col items-center min-w-[160px] max-w-[160px] xl:min-w-[200px] xl:max-w-[200px] text-center gap-1">
                <h3>{status.value}</h3>
                {status.label && (
                  <div className="paragraph-base text-muted-foreground">{parse(status.label)}</div>
                )}
              </div>
            ))}
          </div>
        )}
        {contents?.hasFeaturedItems && (
          <div className="flex flex-col lg:flex-row gap-8 items-center pt-10 justify-center">
            {contents?.featuredItems?.map((featuredItem, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">{featuredItem.icon}</span>
                {featuredItem.content && (
                  <p className="paragraph-lg text-muted-foreground">{parse(featuredItem.content)}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {contents?.image && (
          <div className="relative -mb-[10%] lg:-mb-[20%] mt-10">
            <Image
              src={contents?.image}
              alt={contents?.imageAltText || ""}
              title={contents?.imageAltText || ""}
              className="h-full w-full object-contain object-bottom  aspect-[1170/826] max-w-[1170px] mx-auto"
              width={1170}
              height={826}
              priority
              fetchPriority="high"
            />
          </div>
        )} 
      </div>
    </SectionWrapper>
  );
};

export { Hero20 };
